import { AuthFeatureGuard } from "@/components/elements/AuthFeatureGuard";
import { IconButton } from "@/components/elements/Button";
import {
  NavButton,
  NavDivider,
  Sidebar,
  useSidebar,
} from "@/components/elements/Sidebar";
import {
  AiAssistantIcon,
  AlertsIcon,
  DataSourceIcon,
  EditorIcon,
  HelpIcon,
  OrchestrationIcon,
  ReverseEtlIcon,
  SettingsIcon,
} from "@/components/icons/outline";
import { CurrentUserAvatar } from "@/components/modules/CurrentUserAvatar";
import { useCurrentTabLink } from "@/features/model-tool";
import { useSubscriptionStatus } from "@/features/subscription";
import cn, { default as classNames } from "@/helpers/classNames";
import { isWindows } from "@/helpers/isWindows";
import { useIntercom } from "@/hooks/useIntercom";
import { IntegrationLogoBox } from "@/integrations";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useAssistantSidebar } from "@/pages/ModelTool/model-generator/assistant";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { AccountType, useCurrentUser } from "@/providers/UserProvider";
import { Popover, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { useKBar } from "kbar";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  ActivityIcon,
  BookTextIcon,
  CircleHelpIcon,
  MessageCircleMoreIcon,
  RocketIcon,
} from "lucide-react";
import React, { forwardRef } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { LinkWithSlug, useNavigateWithSlug } from "routes";
import { WorkspaceSwitcher } from "../WorkspaceSwitcher";
import SidebarNotifications, {
  useSidebarNotifications,
} from "../notifications/SidebarNotifications";
import OnboardingChecklistButton from "./OnboardingChecklistButton";
import { TrialBox } from "./TrialBox";

export function WorkspaceSidebar({
  children,
  ...sidebarProps
}: Omit<React.ComponentProps<typeof Sidebar>, "children"> & {
  children?: React.ReactNode;
}) {
  return (
    <Sidebar {...sidebarProps}>
      <div className="flex h-full flex-col border-r border-gray-200 bg-sidebar text-sidebar-foreground dark:border-gray-700">
        <div className="relative h-full overflow-y-auto p-2">
          <WorkspaceSidebarContent />
        </div>
        <div className="flex flex-col px-2 pb-2">
          <NavDivider className="mt-0" />
          <SidebarCollapseButton />
        </div>
      </div>
    </Sidebar>
  );
}

function showTrialBox(
  trial: ReturnType<typeof useSubscriptionStatus>["trial"],
): trial is NonNullable<typeof trial> {
  if (!trial || trial.isExpired) return false;
  return dayjs(trial.trialEndDate).diff(dayjs(), "day") <= 5;
}

export function WorkspaceSidebarContent() {
  const modelToolTabLink = useCurrentTabLink();
  const navigate = useNavigateWithSlug();

  const dwh = useDataWarehouseContext();
  const { trial } = useSubscriptionStatus();
  const { onToggle: onToggleAssistantSidebar } = useAssistantSidebar();
  const showTrial = showTrialBox(trial);
  return (
    <div className="flex h-full w-full flex-col gap-1">
      <div className="flex flex-col gap-1">
        {dwh.features.isExtractEnabled && (
          <NavLink to={"/sources"} icon={<DataSourceIcon />}>
            Data Sources
          </NavLink>
        )}
        {dwh.features.isActivateEnabled && (
          <NavLink to={"/reverse-etl"} icon={<ReverseEtlIcon />}>
            Reverse ETL
          </NavLink>
        )}
        <NavDivider />

        {dwh.features.isTransformEnabled && (
          <>
            <NavLink to={modelToolTabLink} icon={<EditorIcon />}>
              Transform
            </NavLink>
            <NavButton
              onClick={() => {
                navigate({
                  to: modelToolTabLink,
                });
                onToggleAssistantSidebar();
              }}
              icon={<AiAssistantIcon />}
            >
              AI Assistant
            </NavButton>
          </>
        )}
        <NavLink to={"/orchestration"} icon={<OrchestrationIcon />}>
          Orchestration
        </NavLink>
        <NavDivider />
        <AuthFeatureGuard permissions={["manage:all", "read:User"]}>
          <NavLink to={"/settings"} icon={<SettingsIcon />}>
            Settings
          </NavLink>
        </AuthFeatureGuard>
        {showTrial && <TrialBox {...trial} />}
      </div>
      <div className="mb-1 mt-auto flex flex-col gap-1">
        <OnboardingChecklistButton />
        <SidebarNotifications>
          <NotificationNavButton />
        </SidebarNotifications>
        <SidebarHelpDialog>
          <NavButton icon={<HelpIcon />}>Help</NavButton>
        </SidebarHelpDialog>
        <KbarSearchButton />
        <NavDivider unstyled />
        <UserProfileLink />
        <WorkspaceSwitcher allowCreateDestination>
          {(currentAccount) => (
            <WorkspaceSwitcherButton account={currentAccount} />
          )}
        </WorkspaceSwitcher>
      </div>
    </div>
  );
}

function KbarSearchButton() {
  const kbar = useKBar();
  const mixpanel = useMixpanel();
  return (
    <NavButton
      onClick={() => {
        kbar.query.toggle();
        mixpanel.track("Kbar Opened", {
          source: "sidebar",
        });
      }}
      icon={<MagnifyingGlassIcon />}
      tooltip={
        <>
          Search{" "}
          <span className="ml-2 text-background/75">
            {isWindows() ? "Ctrl + k" : "⌘ + k"}
          </span>
        </>
      }
    >
      Search
    </NavButton>
  );
}

function SidebarCollapseButton() {
  const { isCollapsed, toggleCollapseState } = useSidebar();
  return (
    <IconButton
      icon={
        <ChevronLeftIcon
          className={classNames("transition-all", isCollapsed && "rotate-180")}
        />
      }
      onClick={() => toggleCollapseState()}
      variant="ghost"
      size="sm"
      className={classNames("self-end rounded", isCollapsed && "mr-0.5")}
    />
  );
}

function NotificationNavButton() {
  const { notificationsCount, toggleSidebar } = useSidebarNotifications();
  const hasNotifications = notificationsCount > 0;
  const notificationsCountFormatted =
    notificationsCount > 9 ? "9+" : notificationsCount;
  return (
    <NavButton
      icon={
        <div className="relative">
          <AlertsIcon />
          {hasNotifications && (
            <div className="absolute -right-[7px] -top-[8px] flex h-4 min-w-[1rem] items-center justify-center rounded bg-red-500 px-0.5 text-xs font-medium text-white">
              {notificationsCountFormatted}
            </div>
          )}
        </div>
      }
      onClick={() => toggleSidebar()}
    >
      {hasNotifications ? "View alerts" : "No sync alerts"}
    </NavButton>
  );
}

function NavLink(
  props: { to: string } & React.ComponentProps<
    typeof NavButton<typeof LinkWithSlug>
  >,
) {
  return (
    <NavButton
      as={LinkWithSlug}
      {...props}
      getActiveProps={() => ({
        className: "text-blue-500 bg-black/10 dark:bg-white/10",
      })}
    />
  );
}

const SidebarHelpDialog = (props: { children: React.ReactNode }) => {
  const { intercomChat } = useFlags();

  const popperElRef = React.useRef(null);
  let [referenceElement, setReferenceElement] =
    React.useState<HTMLButtonElement | null>();
  let [popperElement, setPopperElement] =
    React.useState<HTMLDivElement | null>();
  let { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "auto",
    modifiers: [
      { name: "preventOverflow", enabled: true },
      { name: "offset", options: { offset: [0, 12] } },
    ],
  });

  const Trigger = React.Children.only(props.children);
  return (
    <Popover>
      <Popover.Button as="div">
        {React.isValidElement<any>(Trigger)
          ? React.cloneElement(Trigger, {
              ref: setReferenceElement,
              role: "button",
            })
          : null}
      </Popover.Button>
      {createPortal(
        <div
          ref={popperElRef}
          style={styles.popper}
          {...attributes.popper}
          className="relative z-50 shadow-lg"
        >
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="scale-95 opacity-0"
            enterTo="scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="scale-100 opacity-100"
            leaveTo="scale-95 opacity-0"
            beforeEnter={() => setPopperElement(popperElRef.current)}
            afterLeave={() => setPopperElement(null)}
          >
            <Popover.Panel className="flex min-w-52 flex-col space-y-2 rounded border bg-popover p-2 text-left text-sm text-popover-foreground">
              <NavButton
                as="a"
                href="https://weld.app/docs"
                target="_blank"
                rel="noreferrer"
                icon={<BookTextIcon className="h-4 w-4" />}
              >
                Documentation
              </NavButton>
              <NavButton
                as="a"
                href="https://weld.app/docs/quickstart-guide"
                target="_blank"
                rel="noreferrer"
                icon={<RocketIcon className="h-4 w-4" />}
              >
                Quick Start Guide
              </NavButton>
              <NavButton
                as="a"
                href="https://weld.app/docs/faq"
                target="_blank"
                rel="noreferrer"
                icon={<CircleHelpIcon className="h-4 w-4" />}
              >
                FAQ
              </NavButton>
              {intercomChat && <IntercomChatHelpItem />}
              <NavButton
                as="a"
                href="https://status.weld.app/"
                target="_blank"
                rel="noreferrer"
                icon={<ActivityIcon className="h-4 w-4" />}
              >
                Status Page
              </NavButton>
            </Popover.Panel>
          </Transition>
        </div>,
        document.body,
      )}
    </Popover>
  );
};

const IntercomChatHelpItem = () => {
  const intercom = useIntercom();
  return (
    <NavButton
      onClick={() => intercom.show()}
      icon={<MessageCircleMoreIcon className="h-4 w-4" />}
    >
      Chat with us
    </NavButton>
  );
};

const WorkspaceSwitcherButton = forwardRef(
  (
    {
      account,
    }: {
      account: AccountType | undefined;
    },
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const ctx = useSidebar();
    return (
      <NavButton
        ref={ref}
        icon={
          account?.dataWarehouse ? (
            <IntegrationLogoBox
              id={account.dataWarehouse.integrationId}
              className={cn("h-8 w-8", {
                "m-[2px]": ctx.isCollapsed,
              })}
            />
          ) : (
            <div />
          )
        }
        className={cn("font-normal", {
          "p-0": ctx.isCollapsed,
        })}
      >
        {account ? (
          <div className="flex w-full flex-col">
            <div className="truncate text-sm font-medium leading-tight dark:text-white">
              {account.name}
            </div>
            <span className="truncate text-xs text-gray-400 dark:text-gray-400">
              {account.dataWarehouse ? (
                <>
                  {account.dataWarehouse?.title}{" "}
                  {account.dataWarehouse?.subTitle &&
                    `(${account.dataWarehouse?.subTitle})`}
                </>
              ) : (
                <em>Data warehouse not connected</em>
              )}
            </span>
          </div>
        ) : (
          <em>No account selected</em>
        )}
      </NavButton>
    );
  },
);

function UserProfileLink() {
  const user = useCurrentUser();
  const ctx = useSidebar();
  return (
    <NavLink
      icon={
        <CurrentUserAvatar
          size="lg"
          className={cn("h-8 w-8", {
            "m-[2px]": ctx.isCollapsed,
          })}
        />
      }
      className={cn("font-normal", {
        "p-0": ctx.isCollapsed,
      })}
      to="/settings/user-profile"
    >
      <div className="flex w-full flex-col">
        <span className="max-w-full truncate font-medium leading-tight">
          {user.firstName ? (
            <span>
              {user.firstName} {user.lastName || ""}
            </span>
          ) : (
            <span className="text-danger">Click to add your name</span>
          )}
        </span>
        <span className="truncate text-xs text-gray-400 dark:text-gray-400">
          {user.email}
        </span>
      </div>
    </NavLink>
  );
}
